import { Injector, Provider, Type, WritableSignal } from '@angular/core';
import { RouteMappingOptions } from '@mwe/models';
import { RawOrSignalOrObservable, ResolvedFactories, ValueOrFactory } from '../util/util.types';
import { WizardContext } from '../service/wizard.context';
import { StepCondition, TypedCondition } from './wizard-condition.model';
import { Observable } from 'rxjs';
import { WizardEvent } from './wizard.events';
import { InferSignalTypes, SignalInputsOf } from '@mwe/utils';
import { ButtonComponent } from '@mwe/ui/components';

/** Allow Factory (might be required for b2b / privat routing) runs inside useFactory so is inside injection context */
export type WizardStepRouting = RouteMappingOptions; // | (() => RouteMappingOptions);

export type WizardStepIdentifier = string; // eslint-disable-line sonarjs/redundant-type-aliases

export type IWizardStepDefinitionOrExtension<Component = unknown> = IWizardStepDefinition<Component> | IWizardStepExtension<Component>;

export type IWizardStepExtension<Component = unknown> = Partial<IWizardStepDefinition<Component>> & {
  extends: WizardStepIdentifier;
};

export type WizardHeaderTitle = RawOrSignalOrObservable<string>;

/**
 * A Step can have several states
 * i.E:
 * - A skipped step can also be completed if predefined values are valid i.E. Address Selection.
 * - A step can be completed, pristine and also entered if the user clicks next on a step with preset values.
 * Use bitwise comparisation. A little like unix permissions
 * I.E:
 * 0001 = 1 = PRISTINE
 * 0010 = 2 = COMPLETED
 * 0011 = 3 = PRISTINE & COMPLETED
 */
export type StepState = number; // eslint-disable-line sonarjs/redundant-type-aliases
export enum StepStateEnum {
  SKIPPED = 4, // step has been skipped
  ENTERED = 8, // step has been entered
  REVISITING = 16, // @TODO check if relevant set if you have entered a step before but skip back (used to check if you are currently revisiting a step will be unset if you are not inside the step currently)
  VALID = 64, // step is valid
}

export interface IWizardStepDefinition<Component = unknown> {
  /** Unique identifier of the step */
  stepId: WizardStepIdentifier /** @TODO we can generate this. On the other hand it would be clever to have user provided identifiers to identify steps with */;
  /** Label used in Progressbar */
  label: RawOrSignalOrObservable<string>;
  /**
   * Component used to render the step.
   * Will be wrapped by <mwe-wizard-step-wrapper> to provide additional contextual functionality and wrap the component into <mwe-wizard-step> unless omitStepWrapper is set to true.
   */
  component: Type<Component>;
  /** Routing configuration. If not set will default to { path: step.stepId } */
  route?: WizardStepRouting;
  /** Wizard will not wrap the step component with <mwe-wizard-step> which provides default controls like progress bar, next / prev buttons */
  omitStepWrapper?: boolean;
  /** When omitStepWrapper is true, setting keepHeader to true keeps the wizard header visible */
  keepHeader?: boolean;
  /** If the step is active it will override the Header title */
  headerTitle?: WizardHeaderTitle;
  /** Changes the Page title */
  pageTitle?: string;

  /** Headline displayed at the top of the step. If empty will not show this headline */
  stepHeadline?: RawOrSignalOrObservable<string>;
  stepHeadlineSubText?: RawOrSignalOrObservable<string>;
  dependencies?: ValueOrFactory<(TypedCondition | StepCondition)[]>;
  /** Dependencies that will be checked before the step is skipped. If fulfilled the step will be skipped. A Step will NOT be skipped if the navigation type is JUMP (i.E. Progress Bar) or PREV (Navigating back) */
  skipIfFulfilled?: ValueOrFactory<(TypedCondition | StepCondition)[]>;
  /** Validators */
  validators?: ValueOrFactory<(TypedCondition | StepCondition)[]>;
  /**
   * If true, this step will not be considered a part of the process.
   * It will not be shown in the progress bar and not considered when determining the next step.
   * Using an observable will allow dynamically add or remove a step
   * */
  omitted?: ValueOrFactory<(TypedCondition | StepCondition)[]>;
  /**
   * Angular supports route provides, so using step.route would be possible. The issue is, that we would not have those providers available in the resolve phase.
   * step.providers will provide in the resolve phase and pass all tokens that are not provided by the route to the route providers using useValue so there is no additional instantiation.
   * */
  providers?: Provider[];
  /** Controls what buttons are displayed. Default is next and prev buttons (will automatically behave based on the currents step position i.e. first / last step) */
  buttons?: ValueOrFactory<RawOrSignalOrObservable<WizardStepButtonDefinition[]>>;
  /** Additional Data that may be accessed at any time via context.step.data */
  data?: Record<string | number | symbol, unknown>;
  /** build tracking data for the current step. runs in injection context */
  buildTrackingData?: (event: WizardEvent) => object | undefined;
  /** If the step is not the first step, this will be used as the default title for the previous button */
  isLegacyStep?: boolean;
  /** A step may be assigned groups. The Wizard can then define several entrypoints that can define the groups of steps to include. If none are specified the 'default' group is assigned */
  groups?: string[];
  /** Will be called when the step is entered */
  onEnter?: (context: WizardContext) => void;
  /** Will be called when the step is exited */
  onExit?: (context: WizardContext) => void;
}

export type IWizardStep = Omit<Required<ResolvedFactories<IWizardStepDefinition>>, 'validators' | 'buildTrackingData'> & {
  providers?: Provider[];
  state: WritableSignal<StepState>;
  validators: TypedCondition[];
  buildTrackingData?: IWizardStepDefinition['buildTrackingData'];
  /** WizardContext for this step. Interaction with the Wizard should preferrably happen through this context. */
  readonly context: WizardContext;
};

type InputsOfButtonComponent = InferSignalTypes<SignalInputsOf<ButtonComponent>>;
type CommonlyUsedButtonComponentInputs = Pick<InputsOfButtonComponent, 'category' | 'color' | 'prependIconClass' | 'appendIconClass'>;
type ButtonRole = 'next' | 'prev' | 'action' | 'text' | 'close';
export type INormalizedWizardStepButton = {
  component: Type<unknown>;
  id: string;
  buttonId?: string; // Id used for data-test
  label?: Observable<string>;
  inputs?: Record<PropertyKey, unknown>;
  onClick?: () => unknown | Observable<unknown> | Promise<unknown>;
  /** Override hide logic */
  hide?: Observable<boolean>;
  /** Override disable logic */
  disable?: Observable<boolean>;
  showSpinner?: Observable<boolean>;
  align?: 'left' | 'right';
  normalized: true;
  /** The injector to use */
  injector: Injector /** Injector contains the config for the button so the component can react. As the parent injector is in the step context it will also contain the WizardContext and step */;
  /** Inputs for ButtonComponent. Will be ignored if a non wizard Button is used (the used component still can use this to pass the inputs) */
  buttonInputs?: Partial<InputsOfButtonComponent>;
  role?: ButtonRole;
} & Partial<CommonlyUsedButtonComponentInputs>;

export type WizardStepButtonDefinition = Omit<
  INormalizedWizardStepButton,
  'injector' | 'hide' | 'disable' | 'normalized' | 'label' | 'showSpinner'
> & {
  hide?: RawOrSignalOrObservable<boolean>;
  disable?: RawOrSignalOrObservable<boolean>;
  label?: RawOrSignalOrObservable<string>;
  showSpinner?: RawOrSignalOrObservable<boolean>;
};

export const SYM_BUTTON_DEFAULTS = Symbol('WIZARD_BUTTON_DEFAULTS');
export type WizardButtonDefaultConfig = {
  [role in ButtonRole]?: Partial<WizardStepButtonDefinition>;
};
